/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useContext } from 'react';

import { ContextualHelp, Grid, Typography2 } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';

import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { useAiSuggestionsContext } from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/AiSuggestionsContext';
import ReviewPartSuggestion from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/ReviewPartSuggestion';

import _t from 'i18n!nls/grading';

type Props = {
  applySuggestion: () => void;
};

const styles = {
  container: () => css`
    padding: var(--cds-spacing-200);
  `,
  labelFlexBox: () => css`
    display: inline-flex;
    align-items: center;
  `,
  labelIcon: () => css`
    margin-right: var(--cds-spacing-50);
  `,
};

const AiSuggestion = ({ applySuggestion }: Props) => {
  const { currentAiReviewPartSuggestion } = useAiSuggestionsContext();
  const { submissionData } = useContext(SGAPageContext);
  const { submission } = submissionData.data || {};

  if (!currentAiReviewPartSuggestion || !submission?.currentRubric) {
    return null;
  }
  return (
    <Grid container css={styles.container}>
      <Grid item xs={12}>
        <ContextualHelp
          helpToggleProps={{
            'aria-label': _t('Information about the rubric recommendations'),
          }}
          tooltipProps={{
            closeButtonProps: {
              'aria-label': _t('Close information about the rubric recommendations'),
            },
          }}
          label={
            <Typography2 component="h3" variant="subtitleMedium">
              <div css={styles.labelFlexBox}>
                <AIGenerateIcon size="small" css={styles.labelIcon} color="interactive" />
                {_t('Recommended for this rubric')}
              </div>
            </Typography2>
          }
        >
          <Typography2 component="p">
            {_t(
              "This recommendation was generated based on the rubric type, question, and learner's submission. You can edit the comment after applying the suggestion."
            )}
          </Typography2>
        </ContextualHelp>
      </Grid>
      {submission?.currentRubric && currentAiReviewPartSuggestion && (
        <Grid item xs={12}>
          <ReviewPartSuggestion
            suggestion={currentAiReviewPartSuggestion}
            applySuggestion={applySuggestion}
            currentRubric={submission.currentRubric}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default AiSuggestion;
